import React, { useState, useEffect } from 'react';
import { Button, Alert, Card, Spinner, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { getBackendUrl } from '../config';
import './Home.scss';

export default function Home() {
  const { user, token } = useAuth();
  const { unreadCount } = useChat();
  const navigate = useNavigate();
  
  const [nextAppointment, setNextAppointment] = useState(null);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const isTutor = user?.role === 'dozent';
        const url = isTutor ? `${getBackendUrl()}/api/anfragen/tutor` : `${getBackendUrl()}/api/anfragen`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const accepted = data.filter(a => a.status === 'akzeptiert' && moment(a.datumZeit).isAfter(moment()));
          accepted.sort((a, b) => new Date(a.datumZeit) - new Date(b.datumZeit));
          if (accepted.length > 0) setNextAppointment(accepted[0]);
          
          const pending = data.filter(a => a.status === 'offen').length;
          setPendingRequests(pending);
        }
      } catch (err) {
        console.error("Dashboard-Fehler:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, user]);

  return (
    <div className="vintage-home p-4">
      <div className="dashboard-header mb-5">
        <h1 className="welcome-header">
          Willkommen zurück, {user?.name || 'Benutzer'}.
        </h1>
        <p className="welcome-subheader">
          Hier ist ein Überblick über deine Lernreise.
        </p>
      </div>

      <Row className="g-4 mb-4">
        {/* Haupt-Widget: Nächster Termin */}
        <Col lg={8} md={12}>
          <Card className="dashboard-card next-appointment-card border-0 shadow-sm h-100">
            <Card.Body className="p-4 d-flex flex-column">
              <h5 className="card-label small text-uppercase fw-bold text-muted mb-4">
                Nächster Termin
              </h5>
              {loading ? (
                <div className="d-flex justify-content-center py-4"><Spinner animation="border" variant="primary" /></div>
              ) : nextAppointment ? (
                <div className="appointment-content d-flex flex-column flex-md-row align-items-md-center">
                  <div className="date-badge me-md-4 mb-3 mb-md-0">
                    <div className="month">{moment(nextAppointment.datumZeit).format('MMM')}</div>
                    <div className="day">{moment(nextAppointment.datumZeit).format('DD')}</div>
                  </div>
                  <div className="details flex-grow-1">
                    <h3 className="mb-1">{nextAppointment.fach?.name}</h3>
                    <p className="mb-0 text-muted">
                      <i className="bi bi-person me-2"></i>
                      {user?.role === 'dozent' ? nextAppointment.schueler?.vollerName : nextAppointment.tutor?.vollerName}
                    </p>
                    <p className="mb-0 text-muted">
                      <i className="bi bi-clock me-2"></i>
                      {moment(nextAppointment.datumZeit).format('HH:mm')} Uhr ({nextAppointment.dauerMinuten} Min.)
                    </p>
                  </div>
                  <Button 
                    className="action-btn mt-3 mt-md-0 ms-md-3"
                    onClick={() => navigate('/calendar')}
                  >
                    Zum Kalender
                  </Button>
                </div>
              ) : (
                <div className="empty-state py-3">
                  <p className="text-muted mb-3">Keine anstehenden Termine gefunden.</p>
                  <Button variant="outline-primary" onClick={() => navigate('/suche')}>
                    Dozenten suchen
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Status-Spalte */}
        <Col lg={4} md={12}>
          <div className="d-flex flex-column gap-4 h-100">
            <Card className="dashboard-card border-0 shadow-sm bg-primary text-white p-3" onClick={() => navigate('/chat/0')} style={{ cursor: 'pointer' }}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="small text-uppercase opacity-75">Nachrichten</div>
                  <div className="h2 mb-0 fw-bold">{unreadCount}</div>
                </div>
                <i className="bi bi-chat-quote h1 mb-0 opacity-50"></i>
              </div>
            </Card>

            <Card className="dashboard-card border-0 shadow-sm p-3" onClick={() => navigate('/buchungen')} style={{ cursor: 'pointer' }}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="small text-uppercase text-muted">Offene Anfragen</div>
                  <div className="h2 mb-0 fw-bold" style={{ color: '#b95c50' }}>{pendingRequests}</div>
                </div>
                <i className="bi bi-calendar-event h1 mb-0 text-muted opacity-25"></i>
              </div>
            </Card>
          </div>
        </Col>
      </Row>

      {/* Hilfe/Info Section */}
      <div className="mt-5 pt-4 border-top">
        <Row className="align-items-center">
          <Col md={8}>
            <h4>Bereit für die nächste Lerneinheit?</h4>
            <p className="text-muted">Finde die passenden Experten für deine individuellen Lernziele.</p>
          </Col>
          <Col md={4} className="text-md-end">
            <Button size="lg" className="search-cta" onClick={() => navigate('/suche')}>
              Dozenten finden
            </Button>
          </Col>
        </Row>
      </div>
    </div>
  );
}
